/**
 * Управление участниками топика — современный KB-стиль.
 *
 * Состав:
 *  - Header: иконка/аватар топика (или эмодзи), название, kind chip, кнопка X
 *  - Search bar (общий — для обеих секций)
 *  - Tabs: «В группе (N)» / «Добавить»
 *  - В членах: карточка с avatar, именем, должностью, ролью; ховер-actions справа
 *  - В добавлении: карточка как чат-DM, по клику моментально добавляет
 *
 * Все стили вынесены в .tasks.css (.tmm-*) — никакого inline-style supercluster.
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchTopicMembers, addTopicMembers, removeTopicMember,
  setTopicMemberRole, fetchActiveUsers, type ChatTopic,
} from "@/api/dashboard";
import { Icon } from "@/components/prototype/Icon";
import { Avatar } from "@/components/prototype/Avatar";

type Tab = "members" | "add";

const TG_AVATAR_GRADIENTS = [
  "linear-gradient(135deg, #4fae4e 0%, #7bc67e 100%)",
  "linear-gradient(135deg, #3390ec 0%, #61a8f0 100%)",
  "linear-gradient(135deg, #e99a12 0%, #f0b84d 100%)",
  "linear-gradient(135deg, #9c7ce5 0%, #b8a0ec 100%)",
  "linear-gradient(135deg, #00acc1 0%, #4dd0e1 100%)",
  "linear-gradient(135deg, #e85c5c 0%, #ee8585 100%)",
];
function pickAvatarGradient(key: string): string {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return TG_AVATAR_GRADIENTS[h % TG_AVATAR_GRADIENTS.length];
}

const KIND_LABEL: Record<string, string> = {
  private_group: "Закрытая группа",
  public: "Открытый чат",
  dm: "Личный чат",
  drive: "Папка диска",
  tasks: "Папка задач",
  group: "Группа",
  docs: "Документы",
};

export function TopicMembersModal({
  topic, myUserId, canManage, canAssignModerator, onClose,
}: {
  topic: ChatTopic;
  myUserId: string;
  canManage: boolean;
  canAssignModerator: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>("members");
  const [search, setSearch] = useState("");

  const membersQ = useQuery({
    queryKey: ["chat/topic/members", topic.id],
    queryFn: () => fetchTopicMembers(topic.id),
    staleTime: 30_000,
  });
  const usersQ = useQuery({
    queryKey: ["users-active"],
    queryFn: fetchActiveUsers,
    enabled: canManage,
    staleTime: 5 * 60_000,
  });

  const memberIds = useMemo(
    () => new Set((membersQ.data ?? []).map(m => m.user_id)),
    [membersQ.data]
  );

  const filteredMembers = useMemo(() => {
    const list = membersQ.data ?? [];
    if (!search.trim()) return list;
    const q = search.trim().toLowerCase();
    return list.filter(m => (m.display_name ?? "").toLowerCase().includes(q));
  }, [membersQ.data, search]);

  const candidates = useMemo(() => {
    if (!canManage) return [];
    const all = (usersQ.data ?? []).filter(u => u.id !== myUserId && !memberIds.has(u.id));
    if (!search.trim()) return all;
    const q = search.trim().toLowerCase();
    return all.filter(u =>
      (u.display_name ?? "").toLowerCase().includes(q) ||
      (u.position ?? "").toLowerCase().includes(q)
    );
  }, [usersQ.data, memberIds, myUserId, search, canManage]);

  const addMut = useMutation({
    mutationFn: (userIds: string[]) => addTopicMembers(topic.id, userIds),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["chat/topic/members", topic.id] }),
    onError: (e: Error) => alert(`Не удалось добавить: ${e.message}`),
  });
  const removeMut = useMutation({
    mutationFn: (userId: string) => removeTopicMember(topic.id, userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["chat/topic/members", topic.id] }),
    onError: (e: Error) => alert(`Не удалось убрать: ${e.message}`),
  });
  const roleMut = useMutation({
    mutationFn: (vars: { userId: string; role: "member" | "admin" }) =>
      setTopicMemberRole(topic.id, vars.userId, vars.role),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["chat/topic/members", topic.id] }),
    onError: (e: Error) => alert(`Не удалось изменить роль: ${e.message}`),
  });

  const kindLabel = KIND_LABEL[topic.kind ?? ""] ?? "Чат";
  const headerIcon = topic.icon || (topic.kind === "drive" ? "📁" : topic.kind === "tasks" ? "✅" : topic.kind === "docs" ? "📝" : "💬");

  return (
    <div className="tmm-backdrop" onClick={onClose}>
      <div className="tmm-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="tmm-header">
          <div className="tmm-header-icon" aria-hidden="true">{headerIcon}</div>
          <div className="tmm-header-text">
            <div className="tmm-header-title">{topic.name || "Без названия"}</div>
            <div className="tmm-header-sub">
              <span className="tmm-kind-chip">{kindLabel}</span>
              <span className="tmm-dot">·</span>
              <span className="tmm-header-count">
                {membersQ.data ? `${membersQ.data.length} участн${membersQ.data.length === 1 ? "ик" : "ика"}` : "…"}
              </span>
            </div>
          </div>
          <button type="button" className="tmm-close" onClick={onClose} title="Закрыть" aria-label="Закрыть">
            <Icon name="x" size={18}/>
          </button>
        </div>

        {/* Tabs (если canManage и не DM) */}
        {canManage && topic.kind !== "dm" && (
          <div className="tmm-tabs">
            <button
              type="button"
              className={`tmm-tab${tab === "members" ? " is-active" : ""}`}
              onClick={() => setTab("members")}
            >
              В группе{membersQ.data ? ` · ${membersQ.data.length}` : ""}
            </button>
            <button
              type="button"
              className={`tmm-tab${tab === "add" ? " is-active" : ""}`}
              onClick={() => setTab("add")}
            >
              Добавить
            </button>
          </div>
        )}

        {/* Search */}
        <div className="tmm-search-wrap">
          <Icon name="search" size={16}/>
          <input
            className="tmm-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={tab === "add" ? "Найти сотрудника по имени или должности…" : "Поиск по имени…"}
          />
          {search && (
            <button type="button" className="tmm-search-clear" onClick={() => setSearch("")} aria-label="Очистить">
              <Icon name="x" size={14}/>
            </button>
          )}
        </div>

        {/* Body */}
        <div className="tmm-body">
          {tab === "members" ? (
            <div className="tmm-list">
              {membersQ.isLoading && (
                <div className="tmm-empty">Загрузка…</div>
              )}
              {!membersQ.isLoading && filteredMembers.length === 0 && (
                <div className="tmm-empty">
                  {search ? `Никого с «${search}»` : "В группе пока никого нет"}
                </div>
              )}
              {filteredMembers.map(m => {
                const isMe = m.user_id === myUserId;
                const isCreator = topic.created_by_user_id === m.user_id;
                const isAdmin = m.role === "admin";
                return (
                  <div key={m.user_id} className="tmm-row">
                    <Avatar
                      name={m.display_name || "?"}
                      userId={m.user_id}
                      avatarFilename={m.avatar_filename ?? null}
                      size={40}
                      color={pickAvatarGradient(m.user_id)}
                    />
                    <div className="tmm-row-text">
                      <div className="tmm-row-name">
                        <span>{m.display_name || "(без имени)"}</span>
                        {isMe && <span className="tmm-tag tmm-tag-you">вы</span>}
                        {isCreator && <span className="tmm-tag tmm-tag-owner" title="Создатель">создатель</span>}
                        {isAdmin && !isCreator && <span className="tmm-tag tmm-tag-mod">модератор</span>}
                      </div>
                    </div>
                    <div className="tmm-row-actions">
                      {canAssignModerator && !isMe && !isCreator && topic.kind === "private_group" && (
                        <button
                          type="button"
                          className={`tmm-action${isAdmin ? " is-active" : ""}`}
                          onClick={() => roleMut.mutate({
                            userId: m.user_id,
                            role: isAdmin ? "member" : "admin",
                          })}
                          title={isAdmin ? "Снять с модератора" : "Сделать модератором"}
                        >
                          <Icon name="shield" size={16}/>
                        </button>
                      )}
                      {canManage && !isMe && !isCreator && (
                        <button
                          type="button"
                          className="tmm-action tmm-action-danger"
                          onClick={() => {
                            if (confirm(`Убрать ${m.display_name} из «${topic.name}»?`)) {
                              removeMut.mutate(m.user_id);
                            }
                          }}
                          title="Убрать из группы"
                        >
                          <Icon name="trash" size={16}/>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="tmm-list">
              {usersQ.isLoading && (
                <div className="tmm-empty">Загрузка…</div>
              )}
              {!usersQ.isLoading && candidates.length === 0 && (
                <div className="tmm-empty">
                  {search ? `Никого с «${search}»` : "Все доступные сотрудники уже в группе"}
                </div>
              )}
              {candidates.map(u => (
                <button
                  key={u.id}
                  type="button"
                  className="tmm-row tmm-row-clickable"
                  onClick={() => addMut.mutate([u.id])}
                  disabled={addMut.isPending}
                >
                  <Avatar
                    name={u.display_name || "?"}
                    userId={u.id}
                    avatarFilename={u.avatar_filename ?? null}
                    size={40}
                    color={pickAvatarGradient(u.id)}
                  />
                  <div className="tmm-row-text">
                    <div className="tmm-row-name">
                      <span>{u.display_name || "(без имени)"}</span>
                    </div>
                    {u.position && <div className="tmm-row-sub">{u.position}</div>}
                  </div>
                  <div className="tmm-row-actions">
                    <span className="tmm-add-pill">
                      <Icon name="plus" size={14}/> Добавить
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="tmm-footer">
          <button type="button" className="tmm-btn" onClick={onClose}>Закрыть</button>
        </div>
      </div>
    </div>
  );
}
